/**
 * "Andra paket till din bil från denna säljare" — the package variant switcher
 * (P25009-407 AC-3, BR-3).
 *
 * On a package listing, the other packages this dealer published for the same
 * car. In practice that is their price tiers for this car, which is what the
 * tiers in a Hjulprogram were for: budget, mellan, premium, side by side, with
 * the one being viewed marked.
 *
 * Choosing one opens that listing. It does NOT rebuild the current package from
 * the dealer's whole inventory — a package is one line at its own listing's
 * price, so swapping parts in place would mean overriding the price and the
 * parts list on a payment path. The dealer's full range is reached through
 * "Bygg ditt hjul" on their profile instead (P25009-406).
 *
 * Renders nothing at all unless there is something to switch to, so an ordinary
 * listing and a lone package are both unaffected.
 */
import React, { useEffect, useState } from 'react';

import { NamedLink } from '../../components';
import { packagePartsOf } from '../../util/packageBundleCore';
import { listingPageLinkProps } from '../../util/listingSeoUrl';
import { getPackageVariants } from '../../util/api';

import css from './ListingPage.module.css';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('sv-SE');

const SectionPackageVariantsMaybe = props => {
  const { listingId, publicData, currentPrice } = props;

  const [variants, setVariants] = useState([]);

  const make = publicData?.categoryLevel1 || '';
  const model = publicData?.categoryLevel2 || '';
  const isPackage = packagePartsOf(publicData || {}).length > 0;

  useEffect(() => {
    if (!isPackage || !listingId || !make || !model) return undefined;

    /* The listing page can be navigated straight from one package to another,
     * so a late reply from the previous listing must not land on this one. */
    let live = true;
    getPackageVariants({ listingId, make, model })
      .then(res => {
        if (live) setVariants(res?.variants || []);
      })
      .catch(() => {
        /* A switcher that cannot load is a missing convenience, not a broken
         * page. The listing itself is complete without it. */
        if (live) setVariants([]);
      });

    return () => {
      live = false;
    };
  }, [isPackage, listingId, make, model]);

  if (!isPackage || !variants.length) return null;

  return (
    <section className={css.packageVariants}>
      <h2 className={css.packageVariantsTitle}>
        Andra paket till din {make} {model} från samma säljare
      </h2>
      <ul className={css.packageVariantsList}>
        {variants.map(variant => {
          /* Built with the shared helper rather than a hand-made path: the
           * listing route is five segments (category/make/model/slug/id) and a
           * second opinion about that here would rot the moment it changes. */
          const linkProps = listingPageLinkProps({
            id: { uuid: variant.listingId },
            attributes: {
              title: variant.title,
              publicData: {
                categoryLevel1: variant.categoryLevel1,
                categoryLevel2: variant.categoryLevel2,
                adType: variant.adType,
                listingType: variant.listingType,
              },
            },
          });

          return (
          <li key={variant.listingId} className={css.packageVariantItem}>
            <NamedLink {...linkProps} className={css.packageVariantLink}>
              {variant.tierLabel ? (
                <span className={css.packageVariantTier}>{variant.tierLabel}</span>
              ) : null}
              <span className={css.packageVariantTitle}>{variant.title}</span>
              <span className={css.packageVariantPrice}>
                {fmt(variant.price)} {variant.currency}
                {/* Said plainly rather than shown as a coloured arrow: the
                    difference is the reason a buyer looks at this list. */}
                {currentPrice && variant.price !== currentPrice ? (
                  <span className={css.packageVariantDiff}>
                    {variant.price < currentPrice
                      ? ` (${fmt(currentPrice - variant.price)} kr billigare)`
                      : ` (${fmt(variant.price - currentPrice)} kr dyrare)`}
                  </span>
                ) : null}
              </span>
            </NamedLink>
          </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SectionPackageVariantsMaybe;
